// Print dedup guard, persisted to localStorage.
//
// BUG-2322: this used to be an in-memory-only Map, on the assumption that
// print history doesn't need to survive app restarts. Live logs from a
// GPR till show the underlying WS connection to the aggregator gateway
// (cw-socket) crashing and reconnecting repeatedly during real outages
// (Redis connectivity failures on the server side); if the POS app itself
// reloads during one of those windows -- a real possibility over a shift --
// an in-memory guard forgets every order it already printed, and a replayed
// order:update for one of them (see use-websocket.js) reprints it. Backed by
// localStorage instead so a reload doesn't reset the guard.
//
// localStorage access is synchronous, unlike IndexedDB/Dexie -- that matters
// here specifically because claimPrintOnce below must stay a single
// synchronous check-and-set with no internal await (see its own comment);
// switching the backing store to something async would silently reopen the
// exact non-atomic race BUG-1971 closed.
const STORAGE_KEY = "cw_pos_printed_orders_v1";
const MAX_ENTRIES = 20000;
// Persisted entries are capped tighter than the in-memory ones: every claim
// re-serializes the whole set to localStorage synchronously (see persist()
// below), so this bounds that write's cost. The dedup window that actually
// matters here is minutes-to-hours (a reconnect/replay storm), not weeks.
const MAX_PERSISTED_ENTRIES = 2000;
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

const loadPersisted = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Map();
    const entries = JSON.parse(raw);
    return new Map(Array.isArray(entries) ? entries : []);
  } catch (err) {
    console.error("[PrintGuard] Failed to load persisted printed-orders, starting empty:", err);
    return new Map();
  }
};

const persist = () => {
  try {
    let entries = Array.from(printedOrders.entries());
    if (entries.length > MAX_PERSISTED_ENTRIES) {
      entries = entries.slice(entries.length - MAX_PERSISTED_ENTRIES);
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch (err) {
    console.error("[PrintGuard] Failed to persist printed-orders:", err);
  }
};

// Map<orderId, timestamp>
const printedOrders = loadPersisted();

// BUG-2373: localStorage alone doesn't survive the POS's own reset paths --
// logout, till close, the error screen's "Clear & Reload" and the boot-error
// "Clear Cache & Reload" all call localStorage.clear(), which wiped this guard
// right before the post-login pending-order poll re-offered old orders. Mirror
// every claim into a separate IndexedDB database (not "cwpos", whose tables
// till close also clears) and merge it back in on load. Writes are
// fire-and-forget; claimPrintOnce itself stays synchronous against the Map.
const IDB_NAME = "cw_pos_print_guard";
const IDB_STORE = "printed";

const openGuardDb = () =>
  new Promise((resolve) => {
    try {
      if (typeof indexedDB === "undefined" || !indexedDB) return resolve(null);
      const req = indexedDB.open(IDB_NAME, 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(IDB_STORE)) req.result.createObjectStore(IDB_STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });

let guardDbPromise = null;
const getGuardDb = () => {
  if (!guardDbPromise) guardDbPromise = openGuardDb();
  return guardDbPromise;
};

const mirrorToIdb = (orderId, ts) => {
  getGuardDb()
    .then((idb) => {
      if (!idb) return;
      const tx = idb.transaction(IDB_STORE, "readwrite");
      tx.objectStore(IDB_STORE).put(ts, orderId);
    })
    .catch(() => {});
};

// Resolves once IndexedDB entries (if any) are merged into the Map. Never
// rejects and never waits more than ~1.5s, so a broken/blocked IndexedDB can't
// hold up a print.
export const printGuardReady = Promise.race([
  getGuardDb()
    .then(
      (idb) =>
        new Promise((resolve) => {
          if (!idb) return resolve();
          try {
            const req = idb.transaction(IDB_STORE, "readonly").objectStore(IDB_STORE).openCursor();
            let merged = 0;
            req.onsuccess = () => {
              const cursor = req.result;
              if (!cursor) {
                if (merged > 0) persist();
                return resolve();
              }
              if (!printedOrders.has(cursor.key)) {
                printedOrders.set(cursor.key, cursor.value);
                merged += 1;
              }
              cursor.continue();
            };
            req.onerror = () => resolve();
          } catch {
            resolve();
          }
        })
    )
    .catch(() => {}),
  new Promise((resolve) => setTimeout(resolve, 1500)),
]);

/**
 * Checks if the order has already been printed in this session.
 * @param {string} orderId
 * @returns {Promise<boolean>}
 */
export const hasPrintedLocal = async (orderId) => {
  if (!orderId) return false;
  return printedOrders.has(orderId);
};

/**
 * Marks an order as printed.
 * @param {string} orderId
 */
export const markPrintedLocal = async (orderId) => {
  if (!orderId) return;
  const ts = Date.now();
  printedOrders.set(orderId, ts);
  persist();
  mirrorToIdb(orderId, ts);

  // Optional: Auto-cleanup (1% chance to run)
  if (Math.random() < 0.01) {
    cleanOldRecords();
  }
};

/**
 * BUG-1971: atomic check-and-claim for concurrent callers.
 * `await hasPrintedLocal(id)` followed by `await markPrintedLocal(id)` as two
 * separate awaited calls is NOT atomic -- two callers started back-to-back
 * (e.g. via Promise.all, or two event handlers firing within the same tick)
 * both suspend at their own `await` before either reaches the `set`, so both
 * see "not printed" and both proceed. This function has no internal await, so
 * the check and the set happen in one uninterruptible synchronous turn:
 * whichever caller's JS execution reaches it first wins outright.
 * @param {string} orderId
 * @returns {boolean} true if this call just claimed the order, false if another caller already holds it
 */
export const claimPrintOnce = (orderId) => {
  if (!orderId) return true;
  if (printedOrders.has(orderId)) return false;
  const ts = Date.now();
  printedOrders.set(orderId, ts);
  // localStorage.setItem is synchronous -- this keeps the whole function
  // free of an internal await, see the function comment above. The IndexedDB
  // mirror is fire-and-forget and doesn't affect atomicity.
  persist();
  mirrorToIdb(orderId, ts);

  if (Math.random() < 0.01) {
    cleanOldRecords();
  }
  return true;
};

const cleanOldRecords = () => {
  if (printedOrders.size > MAX_ENTRIES) {
    const cutoff = Date.now() - THIRTY_DAYS_MS;
    for (const [id, ts] of printedOrders) {
      if (ts < cutoff) {
        printedOrders.delete(id);
      }
    }
    persist();
  }
};
